import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  Badge,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useBreakpointValue,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FiHeart, FiEye, FiEdit, FiTrash2, FiPlus } from 'react-icons/fi'
import { authState } from '../atoms/auth'
import {
  getCommunityList,
  createCommunity,
  updateCommunity,
  deleteCommunity,
  toggleCommunityLike,
} from '../services/apiService'
import dayjs from 'dayjs'

interface CommunityPost {
  id: number
  userId: number
  username: string
  title: string
  content: string
  viewCount: number
  likeCount: number
  isLiked: boolean
  createdAt: string
  updatedAt: string | null
}

const Community: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const deleteDialog = useDisclosure()
  const [deletingPost, setDeletingPost] = useState<CommunityPost | null>(null)
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const isMobile = useBreakpointValue({ base: true, md: false })

  const [posts, setPosts] = useState<CommunityPost[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit] = useState(10)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingPost, setEditingPost] = useState<CommunityPost | null>(null)
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchPosts = async () => {
    setLoading(true)
    try {
      const { data: res } = await getCommunityList(page, limit)
      if (res.statusCode === 200) {
        setPosts(res.data.communities)
        setTotal(res.data.total)
        setTotalPages(res.data.totalPages)
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('게시글 목록을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPosts()
  }, [page])

  const handleCreate = () => {
    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('게시글을 작성하려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }
    setIsEditMode(false)
    setEditingPost(null)
    setFormTitle('')
    setFormContent('')
    onOpen()
  }

  const handleEdit = (post: CommunityPost) => {
    if (post.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('수정 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setIsEditMode(true)
    setEditingPost(post)
    setFormTitle(post.title)
    setFormContent(post.content)
    onOpen()
  }

  const handleDeleteClick = (post: CommunityPost) => {
    if (post.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('삭제 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    setDeletingPost(post)
    deleteDialog.onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!deletingPost) return

    try {
      const { data: res } = await deleteCommunity(deletingPost.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        deleteDialog.onClose()
        setDeletingPost(null)
        fetchPosts()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formContent.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('제목과 내용을 모두 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setSubmitting(true)
    try {
      if (isEditMode && editingPost) {
        const { data: res } = await updateCommunity(editingPost.id, formTitle, formContent)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchPosts()
        }
      } else {
        const { data: res } = await createCommunity(formTitle, formContent)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          onClose()
          fetchPosts()
        }
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('작성에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleLike = async (post: CommunityPost, e: React.MouseEvent) => {
    e.stopPropagation()
    
    if (!auth.id) {
      toast({
        title: t('로그인 필요'),
        description: t('좋아요를 누르려면 로그인이 필요합니다.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      navigate('/signIn')
      return
    }

    try {
      const { data: res } = await toggleCommunityLike(post.id)
      if (res.statusCode === 200) {
        // 즉시 UI 업데이트
        setPosts(prevPosts =>
          prevPosts.map(p =>
            p.id === post.id
              ? { ...p, likeCount: res.data.likeCount, isLiked: res.data.isLiked }
              : p
          )
        )
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('좋아요 처리에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg">{t('커뮤니티')}</Heading>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="brand"
              onClick={handleCreate}
            >
              {t('글쓰기')}
            </Button>
          </HStack>

          {loading ? (
            <Box textAlign="center" py={10}>
              <Spinner size="xl" />
            </Box>
          ) : posts.length === 0 ? (
            <Box textAlign="center" py={10}>
              <Text color="gray.500">{t('등록된 게시글이 없습니다.')}</Text>
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              {posts.map((post) => (
                <Card key={post.id} cursor="pointer" onClick={() => navigate(`/community/${post.id}`)}>
                  <CardBody>
                    <VStack align="stretch" spacing={3}>
                      <HStack justify="space-between">
                        <Heading size="md">{post.title}</Heading>
                        {post.userId === auth.id && (
                          <HStack spacing={2}>
                            <IconButton
                              aria-label={t('수정')}
                              icon={<FiEdit />}
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleEdit(post)
                              }}
                            />
                            <IconButton
                              aria-label={t('삭제')}
                              icon={<FiTrash2 />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteClick(post)
                              }}
                            />
                          </HStack>
                        )}
                      </HStack>
                      <Text color="gray.600" noOfLines={3}>
                        {post.content}
                      </Text>
                      <HStack justify="space-between" fontSize="sm" color="gray.500">
                        <HStack spacing={4}>
                          <Text>{post.username}</Text>
                          <Text>{dayjs(post.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                        </HStack>
                        <HStack spacing={4}>
                          <HStack spacing={1}>
                            <FiEye />
                            <Text>{post.viewCount}</Text>
                          </HStack>
                          <HStack
                            spacing={1}
                            onClick={(e) => handleLike(post, e)}
                            cursor="pointer"
                            color={post.isLiked ? 'brand.600' : 'gray.500'}
                            _hover={{ color: 'brand.600' }}
                          >
                            <FiHeart fill={post.isLiked ? 'currentColor' : 'none'} />
                            <Text>{post.likeCount}</Text>
                          </HStack>
                        </HStack>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>
              ))}
            </VStack>
          )}

          {totalPages > 1 && (
            <HStack justify="center" spacing={2}>
              <Button
                size="sm"
                onClick={() => setPage(page - 1)}
                isDisabled={page === 1}
              >
                {t('이전')}
              </Button>
              <Text fontSize="sm">
                {page} / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage(page + 1)}
                isDisabled={page >= totalPages}
              >
                {t('다음')}
              </Button>
            </HStack>
          )}
        </VStack>
      </Container>

      {/* 글쓰기/수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditMode ? t('게시글 수정') : t('게시글 작성')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('제목')}</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={t('제목을 입력하세요')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('내용')}</FormLabel>
                <Textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder={t('내용을 입력하세요')}
                  rows={10}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {isEditMode ? t('수정') : t('작성')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default Community
